import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { useTimeout } from 'usehooks-ts';

import {
  useHasPendingUserMessages,
  usePcfraPeopleList,
  usePersonList,
  useProperty,
  usePropertyDetails,
  useUserStore,
} from '~/client/hooks/storeHooks';
import { trackEvent } from '~/client/lib/appInsights';
import { api } from '~/client/lib/trpc';
import { wsReqTimeoutSecs } from '~/client/store/tinybase/ClientSyncStores';
import { useTinybaseStore } from '~/client/store/tinybase/tinybaseStore';
import { isPcfraRoute } from '~/client/utils/navigation';
import { getPropertyValidity } from '~/client/utils/properties';

const syncTimeOutMs = wsReqTimeoutSecs * 1000;
const maxPendingMessagesWaitMs = 5000;

export const useSyncPropertyData = () => {
  const { pathname } = useLocation();
  const { propertyId: propertyIdParam = '' } = useParams();
  const propertyId = parseInt(propertyIdParam);

  const userStore = useUserStore();
  const hasCompletedInitialSync = useTinybaseStore((state) => state.hasCompletedInitialSync);
  const updatePropertyDataSyncState = useTinybaseStore(
    (state) => state.updatePropertyDataSyncState,
  );

  const property = useProperty(propertyId);
  const propertyDetails = usePropertyDetails(propertyId);
  const people = usePersonList(propertyId);
  const pcfraPeople = usePcfraPeopleList(propertyId);
  const hasPendingUserMessages = useHasPendingUserMessages();

  const [syncTimedOut, setSyncTimedOut] = useState(false);
  const [shouldRefetchOnPendingMessagesHandled, setShouldRefetchOnPendingMessagesHandled] =
    useState(false);

  const { isValid } = (property && getPropertyValidity(property)) || {};

  const shouldSync = !!userStore && hasCompletedInitialSync && !!isValid;
  const isPcfra = isPcfraRoute(pathname);

  // On a reconnection, do no trigger a fresh sync if there are pending messages
  const refetchOnReconnect = () => {
    if (!hasPendingUserMessages) {
      return true;
    }
    setShouldRefetchOnPendingMessagesHandled(true);
    return false;
  };

  const { data: { success: nhSuccess } = {}, refetch } = api.propertyDataSync.useQuery(
    { propertyId },
    { enabled: shouldSync && !isPcfra, refetchOnReconnect },
  );

  const { data: { success: pcfraSuccess } = {}, refetch: refetchPcfra } =
    api.pcfraPropertyDataSync.useQuery(
      { propertyId },
      { enabled: shouldSync && isPcfra, refetchOnReconnect },
    );

  const success = isPcfra ? pcfraSuccess : nhSuccess;
  const propertyDataReceived = isPcfra
    ? !!pcfraPeople.length
    : !!propertyDetails || !!people.length;

  const hasSyncError = success === false;
  const isSyncing = shouldSync && !propertyDataReceived && !hasSyncError && !syncTimedOut;

  const handleRefetch = async () => {
    setShouldRefetchOnPendingMessagesHandled(false);
    await (isPcfra ? refetchPcfra : refetch)();
  };

  useTimeout(() => setSyncTimedOut(true), isSyncing ? syncTimeOutMs : null);

  // Stop waiting for pending messages if they have not been
  // processed {maxPendingMessagesWaitMs} milliseconds after regaining a lost connection
  useTimeout(
    handleRefetch,
    shouldRefetchOnPendingMessagesHandled ? maxPendingMessagesWaitMs : null,
  );

  useEffect(() => {
    if (!hasPendingUserMessages && shouldRefetchOnPendingMessagesHandled) {
      void handleRefetch();
    }
  }, [hasPendingUserMessages, shouldRefetchOnPendingMessagesHandled]);

  useEffect(() => {
    if (propertyId || propertyDataReceived) {
      setSyncTimedOut(false);
    }
  }, [propertyId, propertyDataReceived]);

  useEffect(() => {
    updatePropertyDataSyncState({ isSyncing, hasSyncError, syncTimedOut });
  }, [isSyncing, hasSyncError, syncTimedOut]);

  useEffect(() => {
    if (hasSyncError || syncTimedOut) {
      const eventProperties = {
        propertyId,
        isPcfra,
        hasSyncError,
        syncTimedOut,
        propertyDataReceived,
      };
      trackEvent('property-data-sync-issue', eventProperties);
      console.warn('Property data sync issue occurred', eventProperties);
    }
  }, [hasSyncError, syncTimedOut, propertyDataReceived]);
};
